import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Avatar, InputAdornment, IconButton,
} from '@mui/material';
import { LockReset as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Shown in place of the whole app while `mustChangePassword` is set: an administrator
 * chose this person's password, and the server refuses everything else until they
 * replace it. The only ways out are a successful change or signing out.
 */
export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const { user, logout, completePasswordChange } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const problem = passwordProblem(newPassword);
    if (problem) {
      setError(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Choose a password different from the one your administrator set.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/me/password`, {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      completePasswordChange(res.data);
      toast.success('Password updated. A confirmation email has been sent to your address.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update your password.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const visibilityAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
        onClick={() => setShowPasswords((v) => !v)}
        edge="end"
      >
        {showPasswords ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
      bgcolor: 'background.default',
    }}>
      <Card sx={{ maxWidth: 460, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
              <LockIcon />
            </Avatar>
            <Typography variant="h5" fontWeight={700}>
              Set a new password
            </Typography>
            {user?.email && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {user.email}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your administrator set a temporary password for you. Choose your own to continue.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Temporary password"
              type={showPasswords ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              helperText="The password your administrator gave you."
              InputProps={{ endAdornment: visibilityAdornment }}
            />
            <TextField
              fullWidth
              label="New password"
              type={showPasswords ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText={PASSWORD_RULES_TEXT}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type={showPasswords ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={saving}
              sx={{ mt: 2 }}
            >
              {saving ? 'Saving…' : 'Save and continue'}
            </Button>
          </form>
          <Button fullWidth sx={{ mt: 2 }} onClick={handleSignOut}>
            Sign out
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
